// @ts-strict-ignore
import { EventDispatcher } from 'three';
import { StringUtils } from '@shared/util/string-utils';
import { CountryInfoVO, UserVO } from '@user/model/vo/user.vo';
import { ProductionCountryVO } from '@article/model/vo/production-country.vo';
import { ArticleVO } from '@article/model/vo/article.vo';
import { ArrayUtils } from '@shared/util/array-utils';
import { DrillingHoleUtils } from '@3d-scene/model3d/util/drillinghole/drilling-hole-utils';
import { ArticleDataService } from '@article/service/article-data.service';
import { ConnectionAreaVO } from '@shared/vo/meta-element.vo';
import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { ArticleService } from '@article/service/article.service';

@Injectable({ providedIn: 'root' })
export class DrillingHoleCountryManager extends EventDispatcher {
  private static readonly DEFAULT_PRODUCTION_COUNTRY_ISO2: string = 'PL';
  private static readonly PRODUCTION_COUNTRY_SORT_PROPERTY: string = 'name';

  private _productionCountries: ProductionCountryVO[] = [];
  private _allDrillingHoleArticles: ArticleVO[];
  private _productionCountriesUpdated$ = new Subject<void>();

  constructor(private articleDataService: ArticleDataService,
              private articleService: ArticleService) {
    super();
  }

  public get productionCountriesUpdated$(): Observable<void> {
    return this._productionCountriesUpdated$.asObservable();
  }

  private static findProductionCountryByIso2(sourceProductionCountries: ProductionCountryVO[], iso2: String): ProductionCountryVO {
    return sourceProductionCountries.find((productionCountry: ProductionCountryVO) => productionCountry.iso2 === iso2);
  }

  public async loadDrillingHoleArticles(): Promise<void> {
    if (this.allDrillingHoleArticles) {
      return;
    }

    const data = await this.articleDataService.loadAllDrillingHoles().toPromise();
    this.productionCountries = data.productionCountries;
    this.allDrillingHoleArticles = data.articles;
  }

  public findSuitableDrillingHoles(parentArticle: ArticleVO, acceptingArea: ConnectionAreaVO): ArticleVO[] {
    if (!this.allDrillingHoleArticles) {
      return;
    }
    const requiredArticles = this.articleService.findRequiredArticles(parentArticle, acceptingArea, this.allDrillingHoleArticles);

    if (requiredArticles && requiredArticles.length) {
      return requiredArticles;
    }
    return [];
  }

  public get productionCountries(): ProductionCountryVO[] {
    return this._productionCountries;
  }

  public set productionCountries(countries: ProductionCountryVO[]) {
    this._productionCountries = !countries ? [] : countries;
    this._productionCountriesUpdated$.next(undefined);
  }

  public get allDrillingHoleArticles(): ArticleVO[] {
    return this._allDrillingHoleArticles;
  }

  public set allDrillingHoleArticles(value: ArticleVO[]) {
    this._allDrillingHoleArticles = value;
  }

  public getSortedProductionCountryCollection(productionCountriesForArticles) {
    const array = [...productionCountriesForArticles];
    return array.sort((a, b) => {
      return StringUtils.stringCompare(
        a[DrillingHoleCountryManager.PRODUCTION_COUNTRY_SORT_PROPERTY],
        b[DrillingHoleCountryManager.PRODUCTION_COUNTRY_SORT_PROPERTY]
      );
    });
  }

  public findUserDefaultProductionCountryByArticle(
    user: UserVO,
    article: ArticleVO,
    fallbackProductionCountry: ProductionCountryVO
  ): ProductionCountryVO {
    const articleProductionCountries = this.getProductionCountriesForArticle(article, fallbackProductionCountry);
    return this.findUserDefaultProductionCountry(user, articleProductionCountries);
  }

  public findUserDefaultProductionCountry(user: UserVO, sourceProductionCountries): ProductionCountryVO {
    if (ArrayUtils.isEmpty(sourceProductionCountries)) {
      return null;
    }
    // try to find country specified as default production country for the given user
    let selectedProductionCountry: ProductionCountryVO = this.getProductionCountryForUser(user, sourceProductionCountries);
    if (!selectedProductionCountry) {
      // if user has no country (or his country doesn't exists in source countries) - try to find default country
      selectedProductionCountry = DrillingHoleCountryManager.findProductionCountryByIso2(
        sourceProductionCountries,
        DrillingHoleCountryManager.DEFAULT_PRODUCTION_COUNTRY_ISO2
      );
    }
    if (!selectedProductionCountry) {
      // otherwise use first found country
      selectedProductionCountry = sourceProductionCountries[0];
    }
    return selectedProductionCountry;
  }

  private getProductionCountryForUser(user: UserVO, sourceProductionCountries: ProductionCountryVO[]): ProductionCountryVO {
    if (!user || !user.address || !user.address.country) {
      return;
    }
    const userCountry: CountryInfoVO = user.address.country;
    const userProductionCountry: ProductionCountryVO = DrillingHoleCountryManager.findProductionCountryByIso2(
      sourceProductionCountries,
      userCountry.defaultProductionCountry
    );
    return userProductionCountry;
  }

  public filterArticlesByCountries(allDrillingHoleArticles) {
    if (ArrayUtils.isEmpty(allDrillingHoleArticles)) {
      return [];
    }
    return allDrillingHoleArticles.filter((article: ArticleVO) => !ArrayUtils.isEmpty(this.getProductionCountriesForArticle(article)));
  }

  public getProductionCountriesForArticles(allDrillingHoleArticles, fallbackProductionCountry: ProductionCountryVO): ProductionCountryVO[] {
    const productionCountriesForArticles: ProductionCountryVO[] = [];
    allDrillingHoleArticles.forEach((article: ArticleVO) => {
      const articleProductionCountries: ProductionCountryVO[] = this.getProductionCountriesForArticle(article, fallbackProductionCountry);
      ArrayUtils.mergeArrays(productionCountriesForArticles, articleProductionCountries);
    });
    return productionCountriesForArticles;
  }

  public getProductionCountriesForArticle(article: ArticleVO, fallbackProductionCountry: ProductionCountryVO = null): ProductionCountryVO[] {
    const isoValues: string[] = DrillingHoleUtils.getProductionCountryIso2Values(article);
    if (!isoValues) {
      // article is not available in production countries at all
      return !fallbackProductionCountry ? [] : [fallbackProductionCountry];
    }
    if (!isoValues.length) {
      // article available in all production countries
      return this._productionCountries;
    }
    // check production countries specified for the article to exclude countries which don't really belong to production countries
    // just in case countries were incorrectly maintained by admin
    let articleProductionCountries: ProductionCountryVO[] = [];
    isoValues.forEach((isoValue: String) => {
      const productionCountry: ProductionCountryVO = DrillingHoleCountryManager.findProductionCountryByIso2(this._productionCountries, isoValue);
      if (productionCountry && articleProductionCountries.indexOf(productionCountry) < 0) {
        articleProductionCountries.push(productionCountry);
      }
    });
    if (!articleProductionCountries.length && fallbackProductionCountry) {
      articleProductionCountries = [fallbackProductionCountry];
    }
    return articleProductionCountries;
  }
}
